import { createRoute, Link } from '@tanstack/react-router';
import { ShieldX } from 'lucide-react';
import { useCallback } from 'react';

import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/features/auth';
import { UsersScreen } from '@/features/users/users-screen';
import {
  validateUsersSearch,
  type UsersSearch,
} from '@/features/users/users-search';

import { rootRoute } from './root.route';

/**
 * The admin Users screen (spec R7.7).
 *
 * Visible to an Admin only. The check here is a convenience — a non-admin who
 * pastes the URL gets a clean "not available" rather than a wall of 403s — but
 * the endpoints behind it enforce the same rule for real (spec R7.8).
 */
const UsersPage = () => {
  const search = usersRoute.useSearch();
  const navigate = usersRoute.useNavigate();
  const { isAdmin } = useCurrentUser();

  const handleSearchChange = useCallback(
    (next: UsersSearch, replace = false) => {
      void navigate({ to: '/users', search: () => next, replace });
    },
    [navigate],
  );

  if (!isAdmin) {
    return (
      <EmptyState
        icon={ShieldX}
        title="This page is for admins"
        description="Only an administrator can view and manage users."
        action={
          <Button asChild variant="outline">
            <Link to="/">Back to projects</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Users
        </h1>
        <p className="text-sm text-muted-foreground">
          Everyone who has signed in, with their role and whether they can still
          reach Atlas.
        </p>
      </div>

      <UsersScreen search={search} onSearchChange={handleSearchChange} />
    </div>
  );
};

export const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  validateSearch: validateUsersSearch,
  component: UsersPage,
});
